/**
 * Decides whether the signed-in member must confirm their member attributes,
 * and loads the org catalog for that prompt. The "Welcome back" checklist
 * (MissingMandatoryFieldsTrigger) renders it as one of its rows, or on its
 * own when nothing else is missing.
 *
 * The prompt is required when ANY of these holds (and the user is NOT in FTUE):
 *
 *   1. memberAttributesChanged === true
 *      Backend flag: an admin grew a program's audience since the user's last
 *      update (set by PUT /groups/:grpId/memberAttributes when added > 0).
 *   2. The org has a catalog but user.memberAttributes is empty — asked on
 *      every sign-in until they pick something.
 *   3. The catalog holds a value id that is not in memberAttributesAckIds,
 *      i.e. it grew since the user last answered.
 *   4. The catalog holds a list for which the member has NEITHER a selected
 *      value NOR an entry in memberAttributesDeclinedLists — an unanswered
 *      list (see below).
 *
 * Rule 3 used to read "a group exists where the user has zero selections".
 * That is unanswerable for a list nobody is in ("Management: Managers | SLT"),
 * so the prompt came back on EVERY sign-in for anyone who is neither — the
 * session marker below was the only thing holding it off, and it dies with the
 * session. Selecting nothing from a list is now a complete answer, recorded by
 * the backend as the catalog the member acknowledged.
 *
 * Rule 4 does NOT undo that fix, and must never be allowed to. A program's
 * audience is one rule per attribute list, ANDed
 * (audienceMatchingService.matchesAudienceRules → participating.every(...)),
 * so a member who answered only Department and left Office empty matches ZERO
 * programs and lands on an empty dashboard — measured on RDP Creative, whose
 * every program audience carries an Office rule. Rule 4 therefore requires an
 * answer for every list, where "none of these apply to me" IS an answer: the
 * modal offers it explicitly per list and the backend stores the list id in
 * memberAttributesDeclinedLists. Requiring a VALUE from every list instead
 * would recreate the unanswerable-prompt bug exactly.
 *
 * One-off for existing members: anyone who answered BEFORE this shipped has no
 * declined list, so a list they deliberately left empty now reads as
 * unanswered and rule 4 prompts them once. That is the point — it is how the
 * empty-Office members get fixed. Saving once writes the declines and ends it
 * for good, because the save records every list they did not pick a value in.
 *
 * Deferral: saving the org catalog flags every group member, the admin who
 * authored it included. That admin is mid company setup, so the prompt is
 * skipped for the rest of their session (ATTR_AUTHORED_KEY) and returns on
 * the next sign-in. Completion stays required, it just moves.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { API } from '../../../api';
import {
  catalogValueIds,
  filterHiddenMemberAttributes,
  hasUnacknowledgedAttributes,
  unansweredAttributeListIds,
  unwrapEnvelope
} from '../utils/memberAttributes';
import { getUserDetails } from '../../../utils/orgName';
import { useCapabilityReleaseForMembers } from '../../../hooks/useCapabilityRelease';
import type { OrgMemberAttribute } from './MemberAttributesModal';
import { initialRoleAnswers, roleTeams, unansweredRoleTeams, type TeamRolesView } from '../utils/teamRoles';
import {
  isProfileFresh,
  userMemberAttributesUpdate,
  userMemberAttributesChangedUpdate,
  userMemberAttributesAckUpdate,
  userMemberAttributesDeclinedUpdate
} from '../../../redux/slices/user-profile/userProfieSlice';

/** Set for the session when this user just authored the org attribute catalog. */
export const ATTR_AUTHORED_KEY = 'dosen_attr_authored_this_session';

/**
 * Set for the session once the member saved the prompt. The requirement is
 * derived from the profile, so it can still hold right after a save: a group
 * left empty keeps scenario 3 true, and a profile refetch served from the
 * request cache can bring the backend flag back. Without this marker the
 * prompt reopened itself on the spot. With it, a member who saved is done
 * for the session; the next sign-in re-evaluates everything.
 */
export const ATTR_CONFIRMED_KEY = 'dosen_attr_prompt_confirmed';

const readSession = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeSession = (key: string, value: string) => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* sessionStorage can be unavailable (private mode) */
  }
};

export interface MemberAttributesPromptState {
  /** The member must confirm their attributes before going on. */
  required: boolean;
  /** Org catalog (hidden groups filtered out). */
  orgMemberAttributes: OrgMemberAttribute[];
  /** The member's current selection. */
  selectedIds: string[];
  /** Lists the member already answered with "none of these apply to me". */
  declinedListIds: string[];
  /** Call after the attributes were saved so the requirement clears. */
  onSaved: (memberAttributes: string[], declinedListIds: string[]) => void;
  /** "Role in <team>": the set-up roles of the member's teams and their answers. */
  teamRoles: TeamRolesView | null;
}

export default function useMemberAttributesPrompt(): MemberAttributesPromptState {
  const dispatch = useAppDispatch();
  const userDetails = useAppSelector((state) => state.userProfile);
  const { orgId } = getUserDetails();
  const [fetchedOrgMemberAttributes, setFetchedOrgMemberAttributes] = useState<OrgMemberAttribute[]>([]);
  const [confirmedThisSession, setConfirmedThisSession] = useState(
    () => readSession(ATTR_CONFIRMED_KEY) === 'true'
  );

  const profileData = userDetails.data;
  const forMembers = useCapabilityReleaseForMembers();
  // Decide from a profile fetched since this page loaded (the persisted one can
  // be from an earlier visit), and keep the decision through later refetches:
  // the slice keeps the profile in place while one is in flight, and waiting on
  // `loading` unmounted the gate every time, wiping the answers already given.
  const isLoaded = isProfileFresh(userDetails);
  const isFtue = profileData?.personal_details?.ftue === true;

  // Scenario 1: backend explicitly flagged.
  const flaggedByBackend = profileData?.memberAttributesChanged === true;

  // Scenarios 2 and 3: catalog vs what the user selected / acknowledged.
  const orgAttrs = useMemo(
    () => filterHiddenMemberAttributes(profileData?.orgMemberAttributes ?? []),
    [profileData?.orgMemberAttributes]
  );
  const userAttrs = useMemo(() => profileData?.memberAttributes ?? [], [profileData?.memberAttributes]);
  const declinedLists = useMemo(
    () => profileData?.memberAttributesDeclinedLists ?? [],
    [profileData?.memberAttributesDeclinedLists]
  );

  // Scenario 2: the org has a catalog and the member has not engaged with it at
  // all — no value picked AND no list declined. Declining every list is a
  // complete answer and leaves `memberAttributes` empty, so testing the values
  // alone re-asked that member on every sign-in: exactly the Sep 18 loop, since
  // only the session marker held it off and that dies at sign-out. Scenario 4
  // is what now catches a genuinely unanswered list.
  const hasNoAttributes =
    orgAttrs.length > 0 && userAttrs.length === 0 && declinedLists.length === 0;

  // Scenario 3: the catalog grew since the member last answered it.
  const hasUnseenAttributes = useMemo(
    () => hasUnacknowledgedAttributes(orgAttrs, profileData?.memberAttributesAckIds),
    [orgAttrs, profileData?.memberAttributesAckIds]
  );

  // Scenario 4: some list has neither a picked value nor an explicit decline.
  // A program audience ANDs one rule per list, so an unanswered list is a
  // guaranteed non-match against every program that targets it.
  const hasUnansweredList = useMemo(
    () => unansweredAttributeListIds(orgAttrs, userAttrs, declinedLists).length > 0,
    [orgAttrs, userAttrs, declinedLists]
  );

  // Scenario 5: a team the member is in has set-up roles and they have not
  // answered "Role in <team>". A role set up after they answered "None of
  // these" asks them again (utils/teamRoles.ts initialRoleAnswers).
  // Only when the Capability release is on for members (not in admin mode).
  const teamRoles = forMembers ? profileData?.teamRoles ?? null : null;
  const hasUnansweredRole = useMemo(
    () => unansweredRoleTeams(roleTeams(teamRoles, orgAttrs, userAttrs), initialRoleAnswers(teamRoles)).length > 0,
    [teamRoles, orgAttrs, userAttrs]
  );

  // Everyone past FTUE is prompted, admins included: memberAttributes is the
  // field the backend auto-invite matches on, and excluding admins left them
  // silently out of every program. The author of the catalog is deferred.
  const justAuthoredAttributes = readSession(ATTR_AUTHORED_KEY) === 'true';
  const required =
    isLoaded &&
    !isFtue &&
    !justAuthoredAttributes &&
    !confirmedThisSession &&
    (flaggedByBackend || hasNoAttributes || hasUnseenAttributes || hasUnansweredList || hasUnansweredRole);

  // Load the org catalog when the prompt is required and the profile did not
  // ship it (the profile payload carries it for most orgs).
  useEffect(() => {
    if (!required) {
      setFetchedOrgMemberAttributes([]);
      return;
    }
    const fromProfile = profileData?.orgMemberAttributes ?? [];
    if (Array.isArray(fromProfile) && fromProfile.length > 0) {
      setFetchedOrgMemberAttributes(fromProfile);
      return;
    }
    let cancelled = false;
    const fetchAttributesByGrpId = (grpId: string) => {
      API.getGroupMemberAttributes({ grpId })
        .then((res) => {
          if (cancelled) return;
          const body = unwrapEnvelope(res) as { orgMemberAttributes?: OrgMemberAttribute[] };
          const list = body?.orgMemberAttributes;
          setFetchedOrgMemberAttributes(Array.isArray(list) ? list : []);
        })
        .catch(() => {
          if (!cancelled) setFetchedOrgMemberAttributes([]);
        });
    };
    const grpIdFromProfile = profileData?.groups?.[0] || profileData?.groupsAll?.[0];
    if (grpIdFromProfile) {
      fetchAttributesByGrpId(grpIdFromProfile);
      return () => {
        cancelled = true;
      };
    }
    if (!orgId) {
      setFetchedOrgMemberAttributes([]);
      return;
    }
    API.groupsListForUser(orgId)
      .then((res) => {
        if (cancelled) return;
        const data = (res as any)?.data?.data ?? (res as any)?.data;
        const groups = Array.isArray(data) ? data : [];
        const firstGroupId = groups[0]?.id ?? groups[0]?._id;
        if (firstGroupId) fetchAttributesByGrpId(firstGroupId);
        else setFetchedOrgMemberAttributes([]);
      })
      .catch(() => {
        if (!cancelled) setFetchedOrgMemberAttributes([]);
      });
    return () => {
      cancelled = true;
    };
  }, [required, orgId, profileData?.orgMemberAttributes, profileData?.groups, profileData?.groupsAll]);

  const orgMemberAttributes = useMemo(
    () =>
      filterHiddenMemberAttributes(
        (profileData?.orgMemberAttributes?.length ? profileData.orgMemberAttributes : fetchedOrgMemberAttributes) ?? []
      ),
    [profileData?.orgMemberAttributes, fetchedOrgMemberAttributes]
  );

  const onSaved = useCallback(
    (memberAttributes: string[], savedDeclinedListIds: string[]) => {
      dispatch(userMemberAttributesUpdate(memberAttributes));
      dispatch(userMemberAttributesChangedUpdate(false));
      // The BE stamped the live catalog as this member's acknowledgement; mirror
      // it so the requirement clears without waiting for the next profile fetch.
      dispatch(userMemberAttributesAckUpdate(catalogValueIds(orgMemberAttributes)));
      // Same reason for the declines: scenario 4 is derived from the profile,
      // so without this every list answered "none of these" would still read as
      // unanswered until the next fetch.
      dispatch(userMemberAttributesDeclinedUpdate(savedDeclinedListIds ?? []));
      writeSession(ATTR_CONFIRMED_KEY, 'true');
      setConfirmedThisSession(true);
    },
    [dispatch, orgMemberAttributes]
  );

  return {
    required,
    orgMemberAttributes,
    selectedIds: userAttrs,
    declinedListIds: declinedLists,
    onSaved,
    teamRoles
  };
}
